if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        try { this.variantPhotos = JSON.parse(this.querySelector('[data-gallery-variants]')?.textContent || '[]'); }
        catch (_) { this.variantPhotos = []; }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              this.selectVariantForMedia(mediaToSwitch.dataset.target);
            });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.selectVariantForMedia(event.detail.currentElement.dataset.mediaId);
      }

      selectVariantForMedia(mediaId) {
        const sourceId = String(mediaId).split('-').pop();
        const choices = this.variantPhotos.filter(entry => String(entry.mediaId) === sourceId);
        if (!choices.length) return;
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        if (!picker) return;
        const groups = [...picker.querySelectorAll('.product-form__input')].map(group => {
          const select = group.querySelector('select');
          return select ? { select, current: select.value } :
            { group, current: group.querySelector('input[type="radio"]:checked')?.value };
        });
        if (!groups.length) return;
        const choice = choices.filter(entry => entry.options?.length === groups.length)
          .sort((a, b) => b.options.filter((option, index) => option === groups[index].current).length -
            a.options.filter((option, index) => option === groups[index].current).length)[0];
        if (!choice || choice.options.every((option, index) => option === groups[index].current)) return;
        let changed;
        for (const [index, option] of choice.options.entries()) {
          if (option === groups[index].current) continue;
          const group = groups[index];
          if (group.select) {
            const target = [...group.select.options].find(entry => entry.value === option);
            if (!target) return;
            const previous = group.select.querySelector('option[selected]');
            previous?.removeAttribute('selected');
            target.setAttribute('selected', 'selected');
            group.select.value = option;
            changed = group.select;
          } else {
            const target = [...group.group.querySelectorAll('input[type="radio"]')].find(entry => entry.value === option);
            if (!target) return;
            target.checked = true;
            changed = target;
          }
        }
        changed?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // A variant update already has a live image beside the mobile controls.
          // Keep those controls in view; explicit thumbnail interactions keep native scrolling.
          if (prepend && !this.mql.matches &&
              this.closest('product-info')?.querySelector('divine-mobile-options[data-mobile-active="true"]')) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
